'use client'
import { useState, useEffect, useCallback } from 'react'
import { Button, Col, Row, Alert } from 'antd'
import { useRouter } from 'next/router'
import useTranslation from '@/hooks/useTranslation'
import { useDispatch, useSelector } from 'react-redux'
import { profileData, updateProfile } from '@/features/auth/apis/authSlice'
import { setDashboardTitle } from '@/redux/themeSlice'
import { queryCountries } from '@/redux/countriesSlice'
import { UeCountries } from "@/config/StatiMembriDell'UE"
import { errorHandling, openNotification } from '@/utils/utils'

import Layout from '@/components/layout-components/dashboardLayout/Layout'
import PersonalInformation from '@/features/profile/components/PersonalInformation'
import Address from '@/features/profile/components/Address'
import Attachments from '@/features/profile/components/Attachments'
import UploadCard2 from '@/components/shared-components/form/UploadCard2'
import StepDisplay from '@/features/profile/components/StepDisplay'
import DeleteAccount from '@/features/profile/components/DeleteAccount'

const Profile = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  
  useEffect(() => {
    dispatch(setDashboardTitle('profile.title'))
    dispatch(profileData())
  }, [dispatch])

  const { updateProfileLoading, profile } = useSelector((state) => state.auth)
  const { countries } = useSelector((state) => state.countries)
  const isIncompleteProfile = profile?.profile_completed_before !== 1

  // QUERY COUNTRIES
  useEffect(() => {
    dispatch(queryCountries())
  }, [dispatch])

  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)

  // DISPLAY EDIT LOGIC
  const [isEditable, setIsEditable] = useState(false)

  const toggleEdit = useCallback(() => {
    setIsEditable(!isEditable)
  }, [isEditable])

  const [errors, setErrors] = useState({})
  const [alertError, setAlertError] = useState(null)

  // FORM VALUES
  const [formValues, setFormValues] = useState({
    _method: 'PUT',
    user_id: null,
    // PERSONAL INFORMATION
    name: '',
    lname: '',
    email: '',
    mobile_number: '',
    mobile_number_code: '' || '+39',
    gender: null,
    nationality: null,
    country_code: null,
    birth_date: '',
    birth_place: null,
    // ADDRESS
    country: '',
    province: '',
    city: '',
    street: '',
    building_number: '',
    zip_code: '',
    // ATTACHMENTS
    serial_number: null,
    nid_expire_at: null,
    residence_expire_at: null,
  })
  const [profile_photo, setProfile_photo] = useState([])
  const [imageUrl, setImageUrl] = useState('')
  const [national_id_files, setNational_id_files] = useState([])
  const [residence_files, setResidence_files] = useState([])
  const [tax_health_card_files, setTax_health_card_files] = useState([])

  // HANDLE IMAGE UPLOAD
  const handleProfilePhotoUpload = ({ fileList }) => {
    setProfile_photo(fileList)
    setImageUrl(URL.createObjectURL(fileList['0'].originFileObj))

    if (!isEditable) {
      let formData = new FormData()

      if (fileList) {
        formData.append('_method', 'PUT')
        formData.append('profile_photo', fileList['0'].originFileObj)
        dispatch(updateProfile(formData))
          .unwrap()
          .then((data) => {
            openNotification('success', data.message)
          })
          .catch((error) => openNotification('error', errorHandling(error)))
      }
    }
  }

  // SET INITAILS VALUES
  useEffect(() => {
    if (profile) {
      setFormValues({
        _method: 'PUT',
        user_id: profile.id,
        // PERSONAL INFORMATION
        name: profile.name,
        lname: profile.lname,
        email: profile.email,
        mobile_number: profile.mobile_number,
        mobile_number_code: profile.mobile_number_code || '+39',
        gender: profile.gender,
        nationality: profile.nationality || 'Italia',
        country_code: profile.country_code || 'IT',
        birth_date: profile.birth_date?.replace(/-/g, '/'),
        birth_place: profile.birth_place || 'Italia',
        // ADDRESS
        country: profile.country || 'Italia',
        province: profile.province,
        city: profile.city,
        street: profile.street,
        building_number: profile.building_number,
        zip_code: profile.zip_code,
        // ATTACHMENTS
        serial_number: profile.serial_number,
        nid_expire_at: profile.nid_expire_at?.replace(/-/g, '/'),
        residence_expire_at: profile.residence_expire_at?.replace(/-/g, '/'),
      })

      if (profile.profile_photo) {
        setImageUrl(
          `${process.env.NEXT_PUBLIC_IMG_PATH}${profile?.profile_photo}`
        )
      } else {
        setImageUrl('/imgs/profile-img.jpg')
      }
    }
  }, [profile, isEditable])

  const profileProps = {
    profile,
    formValues,
    setFormValues,
    errors,
    national_id_files,
    setNational_id_files,
    tax_health_card_files,
    setTax_health_card_files,
    residence_files,
    setResidence_files,
  }

  // STEPS LOGIC
  const [current, setCurrent] = useState(0)

  const steps = [
    {
      title: t('Personal Information'),
      content: <PersonalInformation {...profileProps} />,
    },
    {
      title: t('Address'),
      content: <Address {...profileProps} />,
    },
    {
      title: t('Attachments'),
      content: <Attachments {...profileProps} />,
    },
  ]

  useEffect(() => {
    setAlertError(null)
  }, [current])

  const StepOneValidation = (data, currentVal) => {
    if (
      data.name &&
      data.lname &&
      data.email &&
      data.mobile_number &&
      data.mobile_number_code &&
      data.gender &&
      data.nationality &&
      data.country_code &&
      data.birth_date &&
      data.birth_place
    ) {
      currentVal ? setCurrent(currentVal) : setCurrent(current + 1)
      router.replace({
        query: { ...router.query, page: 2 },
      })
    } else {
      let errorsobj = {}
      if (!data.name) errorsobj = { ...errorsobj, name: ' ' }
      if (!data.lname) errorsobj = { ...errorsobj, lname: ' ' }
      if (!data.email) errorsobj = { ...errorsobj, email: ' ' }
      if (!data.mobile_number) errorsobj = { ...errorsobj, mobile_number: ' ' }
      if (!data.mobile_number_code)
        errorsobj = { ...errorsobj, mobile_number_code: ' ' }
      if (!data.gender) errorsobj = { ...errorsobj, gender: ' ' }
      if (!data.nationality) errorsobj = { ...errorsobj, nationality: ' ' }
      if (!data.country_code) errorsobj = { ...errorsobj, country_code: ' ' }
      if (!data.birth_date) errorsobj = { ...errorsobj, birth_date: ' ' }
      if (!data.birth_place) errorsobj = { ...errorsobj, birth_place: ' ' }
      setErrors(errorsobj)
      setAlertError('Tutti i campi sono obbligatori')
    }
  }
  const StepTwoValidation = (data, currentVal) => {
    if (
      data.country &&
      data.province &&
      data.city &&
      data.street &&
      data.building_number &&
      data.zip_code
    ) {
      currentVal ? setCurrent(currentVal) : setCurrent(current + 1)
      router.replace({
        query: { ...router.query, page: 3 },
      })
    } else {
      let errorsobj = {}
      if (!data.country) errorsobj = { ...errorsobj, country: ' ' }
      if (!data.province) errorsobj = { ...errorsobj, province: ' ' }
      if (!data.city) errorsobj = { ...errorsobj, city: ' ' }
      if (!data.street) errorsobj = { ...errorsobj, street: ' ' }
      if (!data.building_number)
        errorsobj = { ...errorsobj, building_number: ' ' }
      if (!data.zip_code) errorsobj = { ...errorsobj, zip_code: ' ' }
      setErrors(errorsobj)
      setAlertError('Tutti i campi sono obbligatori')
    }
  }
  const StepThreeValidation = (data) => {
    if (
      data.serial_number &&
      data.nid_expire_at &&
      data.national_id_files.length > 0 &&
      data.tax_code_health_card_files.length > 0
    ) {
      if (
        (!UeCountries.includes(formValues.nationality) &&
          data.residence_files &&
          data.residence_expire_at) ||
        UeCountries.includes(formValues.nationality)
      ) {
        router.push('/dashboard/servizi')
      } else {
        let errorsobj = {}
        if (data.national_id_files.length === 0)
          errorsobj = { ...errorsobj, national_id_files: ' ' }
        if (!data.nid_expire_at)
          errorsobj = { ...errorsobj, nid_expire_at: ' ' }

        if (data.residence_files.length === 0)
          errorsobj = { ...errorsobj, residence_files: ' ' }
        if (!data.residence_expire_at)
          errorsobj = { ...errorsobj, residence_expire_at: ' ' }

        if (data.tax_code_health_card_files.length === 0)
          errorsobj = { ...errorsobj, tax_code_health_card_files: ' ' }
        if (!data.serial_number)
          errorsobj = { ...errorsobj, serial_number: ' ' }

        setErrors(errorsobj)
        setAlertError('Tutti i campi sono obbligatori')
      }
    } else {
      let errorsobj = {}
      if (data.national_id_files.length === 0)
        errorsobj = { ...errorsobj, national_id_files: ' ' }
      if (!data.nid_expire_at) errorsobj = { ...errorsobj, nid_expire_at: ' ' }

      if (data.residence_files.length === 0)
        errorsobj = { ...errorsobj, residence_files: ' ' }
      if (!data.residence_expire_at)
        errorsobj = { ...errorsobj, residence_expire_at: ' ' }

      if (data.tax_code_health_card_files.length === 0)
        errorsobj = { ...errorsobj, tax_code_health_card_files: ' ' }
      if (!data.serial_number) errorsobj = { ...errorsobj, serial_number: ' ' }

      setErrors(errorsobj)
      setAlertError('Tutti i campi sono obbligatori')
    }
  }

  const advanceToNextStep = (nextPage) => {
    setCurrent(current + 1)
    router.replace({
      query: { ...router.query, page: nextPage },
    })
  }

  const next = () => {
    let formData = new FormData()

    formData.set('_method', 'PUT')
    formData.set('user_id', profile.id)

    if (profile_photo[0]) {
      formData.set('profile_photo', profile_photo['0'].originFileObj)
    }

    if (current === 0) {
      formValues.name && formData.set('name', formValues.name)
      formValues.lname && formData.set('lname', formValues.lname)
      formValues.email && formData.set('email', formValues.email)
      formValues.mobile_number &&
        formData.set('mobile_number', formValues.mobile_number)
      formValues.mobile_number_code &&
        formData.set('mobile_number_code', formValues.mobile_number_code)

      countries.forEach((country) => {
        if (`+${country.country_code}` === formValues.mobile_number_code) {
          formData.set('country_short_name', country.short_name)
        }
      })

      formValues.gender && formData.set('gender', formValues.gender)
      formValues.nationality &&
        formData.set('nationality', formValues.nationality)
      formValues.country_code &&
        formData.set('country_code', formValues.country_code)
      formValues.birth_date &&
        formData.set('birth_date', formValues.birth_date.replace(/\//g, '-'))
      formValues.birth_place &&
        formData.set('birth_place', formValues.birth_place)
    }

    if (current === 1) {
      formValues.country && formData.set('country', formValues.country)
      formValues.province && formData.set('province', formValues.province)
      formValues.city && formData.set('city', formValues.city)
      formValues.street && formData.set('street', formValues.street)
      formValues.building_number &&
        formData.set('building_number', formValues.building_number)
      formValues.zip_code && formData.set('zip_code', formValues.zip_code)
    }

    if (current === 2) {
      formValues.serial_number &&
        formData.set('serial_number', formValues.serial_number)
      formValues.nid_expire_at &&
        formData.set(
          'nid_expire_at',
          formValues.nid_expire_at.replace(/\//g, '-')
        )
      formValues.residence_expire_at &&
        formData.set(
          'residence_expire_at',
          formValues.residence_expire_at.replace(/\//g, '-')
        )

      const appendFilesPath = (fieldName, files) => {
        files.forEach((file) => {
          formData.append(`${fieldName}[]`, file)
        })
      }

      appendFilesPath('national_id_files', national_id_files)
      appendFilesPath('residence_files', residence_files)
      appendFilesPath('tax_health_card_files', tax_health_card_files)
    }

    dispatch(updateProfile(formData))
      .unwrap()
      .then((data) => {
        setAlertError(null)
        setErrors({})

        setNational_id_files([])
        setResidence_files([])
        setTax_health_card_files([])

        const isPersonalInformationStep = current === 0
        const isAddressStep = current === 1
        const isAttachmentsStep = current === 2

        if (isIncompleteProfile) {
          if (isPersonalInformationStep) {
            advanceToNextStep(2)
            return
          }

          if (isAddressStep) {
            advanceToNextStep(3)
            return
          }

          if (isAttachmentsStep) {
            const becameComplete = data.profile?.profile_completed_before === 1

            if (becameComplete) {
              router.push('/dashboard/servizi')
              return
            }

            openNotification('success', data.message)
            setIsEditable(false)
          }

          return
        }

        if (isPersonalInformationStep) {
          StepOneValidation(data.profile)
        }
        if (isAddressStep) {
          StepTwoValidation(data.profile)
        }
        if (isAttachmentsStep) {
          StepThreeValidation(data.profile)
        }
      })
      .catch((error) => {
        const message = errorHandling(error)
        openNotification('error', message)

        if (error.response) {
          const errors = error.response.data.errors
          let errorsobj = {}

          for (const key in errors) {
            errorsobj = { ...errorsobj, [key]: errors[key][0] }
          }

          setErrors(errorsobj)
          setAlertError(
            isIncompleteProfile ? message : 'Tutti i campi sono obbligatori'
          )
        }
      })
  }

  // LINKS LOGIC
  const { page } = router.query

  useEffect(() => {
    if (page) {
      const index = page - 1
      setCurrent(index)
    }
    setAlertError(null)
  }, [page])

  return (
    <>
      <div className='px-16 md:px-34 py-16 md:py-32'>
        <Row justify='center' gutter={[24, 24]}>
          <Col xs={24} sm={10} md={8} lg={6}>
            <div>
              <UploadCard2
                title={t('Change Profile photo')}
                subTitle={t('Don’t get more than 5MB such JPG, PNG, JPEG.')}
                onChange={handleProfilePhotoUpload}
                imageUrl={imageUrl}
              />
              <Button
                className='hidden sm:block hover:bg-danger_50 !text-danger text-base font-semibold border-0 shadow-lg rounded-lg py-14 mt-24 h-auto w-full'
                onClick={() => setDeleteAccountOpen(true)}
              >
                {t('Delete Account')}
              </Button>
            </div>
          </Col>
          <Col xs={24} lg={18}>
            {isEditable ? (
              <>
                <div className='bg-[#FDFDFD] border border-[#DDD] rounded-xl p-24'>
                  {steps[current].content}
                </div>
                {alertError && (
                  <Alert
                    className='mt-16'
                    message={alertError}
                    type='error'
                  />
                )}
                <div className='flex justify-end mt-16'>
                  <Button
                    className='btn-green font-base font-semibold py-14 px-40 h-auto'
                    onClick={next}
                    loading={updateProfileLoading}
                  >
                    {current !== 2 ? t('Save & Continue') : t('Save')}
                  </Button>
                </div>
              </>
            ) : (
              <StepDisplay {...{ current, toggleEdit }} />
            )}
          </Col>
        </Row>
        {!isEditable && (
          <Button
            className='block sm:hidden hover:bg-danger_50 !text-danger text-base font-semibold border-0 shadow-lg rounded-lg py-14 mt-24 h-auto w-full'
            onClick={() => setDeleteAccountOpen(true)}
          >
            {t('Delete Account')}
          </Button>
        )}
      </div>
      <DeleteAccount open={deleteAccountOpen} setOpen={setDeleteAccountOpen} />
    </>
  )
}

Profile.Layout = Layout
export default Profile
